import React, { useState, useRef } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';
import TextareaWithLines from '../components/TextareaWithLines';
import PreWithLines from '../components/PreWithLines';

const CsharpToJsonPage = () => {
  const [csharpInput, setCsharpInput] = useState('');
  const [jsonOutput, setJsonOutput] = useState('');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Example preset
  const csharpExample = `public class RootObject
{
    public string Name { get; set; }
    public int Age { get; set; }
    public string Email { get; set; }
    public bool IsEmployed { get; set; }
    public Address Address { get; set; }
    public List<PhoneNumbersItem> PhoneNumbers { get; set; }
    public List<string> Tags { get; set; }
    public double Height { get; set; }
    public string DateOfBirth { get; set; }
    public object NullValue { get; set; }
    public int[] IntArray { get; set; }
    public string[] StringArray { get; set; }
    public Dictionary<string, object> Properties { get; set; }
    public bool? OptionalFlag { get; set; }
}

public class Address
{
    public string Street { get; set; }
    public string City { get; set; }
    public string ZipCode { get; set; }
    public List<string> Tags { get; set; }
}

public class PhoneNumbersItem
{
    public string Type { get; set; }
    public string Number { get; set; }
    public List<string> Extensions { get; set; }
}`;
  
  // Refs for scroll syncing
  const inputRef = useRef(null);
  const outputRef = useRef(null);
  const inputLineNumbersRef = useRef(null);
  const outputLineNumbersRef = useRef(null);
  const [isInputScrolling, setIsInputScrolling] = useState(false);
  const [isOutputScrolling, setIsOutputScrolling] = useState(false);

  // Enhanced scroll synchronization
  const handleInputScroll = () => {
    if (inputLineNumbersRef.current && inputRef.current) {
      inputLineNumbersRef.current.scrollTop = inputRef.current.scrollTop;
    }

    if (!isOutputScrolling && outputRef.current && inputRef.current) {
      setIsInputScrolling(true);
      const inputScrollTop = inputRef.current.scrollTop;
      const inputScrollHeight = inputRef.current.scrollHeight;
      const inputClientHeight = inputRef.current.clientHeight;
      
      const outputScrollHeight = outputRef.current.scrollHeight;
      const outputClientHeight = outputRef.current.clientHeight;
      
      const scrollRatio = inputScrollTop / (inputScrollHeight - inputClientHeight || 1);
      const outputScrollTop = scrollRatio * (outputScrollHeight - outputClientHeight || 1);
      
      outputRef.current.scrollTop = outputScrollTop;
      
      if (outputLineNumbersRef.current) {
        outputLineNumbersRef.current.scrollTop = outputScrollTop;
      }
      
      setTimeout(() => setIsInputScrolling(false), 50);
    }
  };

  const handleOutputScroll = () => {
    if (outputLineNumbersRef.current && outputRef.current) {
      outputLineNumbersRef.current.scrollTop = outputRef.current.scrollTop;
    }

    if (!isInputScrolling && inputRef.current && outputRef.current) {
      setIsOutputScrolling(true);
      const outputScrollTop = outputRef.current.scrollTop;
      const outputScrollHeight = outputRef.current.scrollHeight;
      const outputClientHeight = outputRef.current.clientHeight;
      
      const inputScrollHeight = inputRef.current.scrollHeight;
      const inputClientHeight = inputRef.current.clientHeight;
      
      const scrollRatio = outputScrollTop / (outputScrollHeight - outputClientHeight || 1);
      const inputScrollTop = scrollRatio * (inputScrollHeight - inputClientHeight || 1);
      
      inputRef.current.scrollTop = inputScrollTop;
      
      if (inputLineNumbersRef.current) {
        inputLineNumbersRef.current.scrollTop = inputScrollTop;
      }
      
      setTimeout(() => setIsOutputScrolling(false), 50);
    }
  };

  const convertCsharpToJson = () => {
    try {
      setError('');
      
      if (!csharpInput.trim()) {
        setError('Please enter a C# class definition');
        return;
      }
      
      // Parse C# classes
      const classDefinitions = extractClassDefinitions(csharpInput);
      
      if (Object.keys(classDefinitions).length === 0) {
        setError('No valid C# classes found in the input');
        return;
      }
      
      // Determine the root class and generate JSON
      const rootClass = findRootClass(classDefinitions);
      const jsonObject = generateJsonFromClass(rootClass, classDefinitions);
      
      // Ensure empty arrays are shown as [] not null
      const processedJson = ensureArraysAreNotNull(jsonObject);
      
      // Convert to pretty JSON string
      const jsonStr = JSON.stringify(processedJson, null, 2);
      
      setJsonOutput(jsonStr);
    } catch (e) {
      setError('Error converting C# class: ' + e.message);
    }
  };
  
  // C# kodundan tüm sınıf tanımlarını ayıklayan fonksiyon
  const extractClassDefinitions = (csharpCode) => {
    const classDefinitions = {};
    
    // Önce tüm sınıf tanımlarını bul
    const classRegex = /public\s+class\s+(\w+)(?:\s*:\s*\w+)?\s*{([^{}]*(?:{[^{}]*(?:{[^{}]*}[^{}]*)*}[^{}]*)*)}/gs;
    let classMatch;
    
    while ((classMatch = classRegex.exec(csharpCode)) !== null) {
      const className = classMatch[1];
      const classBody = classMatch[2];
      
      // Sınıfa ait özellikleri çıkar
      const properties = extractPropertiesFromClassBody(classBody);
      classDefinitions[className] = properties;
    }
    
    return classDefinitions;
  };
  
  // Extract properties from class body
  const extractPropertiesFromClassBody = (classBody) => {
    const properties = [];
    
    // More comprehensive regex to capture all property types
    // Enhanced for arrays (Type[]), generic types, and namespaced types
    const propertyRegex = /public\s+((?:[a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)*(?:<[^>]+>)?(?:\[\])?|\[[^\]]+\]))\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*{\s*get;\s*set;\s*}/g;
    let propertyMatch;
    
    while ((propertyMatch = propertyRegex.exec(classBody)) !== null) {
      const fullType = propertyMatch[1].trim();
      const propertyName = propertyMatch[2].trim();
      
      // Identify List/Array/Collection types
      const isArrayType = fullType.endsWith('[]');
      const hasGeneric = fullType.includes('<') && fullType.includes('>');
      
      let baseType, genericParam, isList;
      
      if (isArrayType) {
        // T[] style array
        baseType = 'Array';
        genericParam = fullType.substring(0, fullType.length - 2);
        isList = true;
      } else if (hasGeneric) {
        // List<T> or similar generic collection
        const genericMatch = fullType.match(/<([^>]+)>/);
        baseType = fullType.split('<')[0];
        genericParam = genericMatch ? genericMatch[1] : null;
        
        // Check for collection types
        isList = isCollectionType(baseType);
      } else {
        // Normal type
        baseType = fullType;
        genericParam = null;
        isList = isCollectionType(baseType);
      }
      
      properties.push({
        name: propertyName,
        fullType: fullType,
        baseType: baseType,
        genericParam: genericParam,
        isList: isList || isArrayType
      });
    }
    
    return properties;
  };
  
  // Helper function to check if a type is a collection
  const isCollectionType = (type) => {
    const collectionTypes = [
      'list', 'enumerable', 'collection', 'array', 'set', 'iqueryable',
      'ilist', 'icollection', 'ienumerable', 'iset', 'hashset',
      'queue', 'stack', 'linkedlist', 'concurrentbag', 'blockingcollection',
      'readonlycollection', 'readonlylist', 'ireadonlycollection', 'ireadonlylist'
    ];
    
    return collectionTypes.some(collType => 
      type.toLowerCase().includes(collType)
    );
  };
  
  // Find the root class (typically has "Root" in the name or is the first class)
  const findRootClass = (classDefinitions) => {
    const classNames = Object.keys(classDefinitions);
    
    // Prioritize names containing "Root"
    const rootClass = classNames.find(name => 
      /root/i.test(name)
    );
    
    // If no class contains "root", use the first class as root
    return rootClass || classNames[0];
  };
  
  // Generate a JSON object structure for a class
  const generateJsonFromClass = (className, classDefinitions) => {
    const result = {};
    
    // Return empty object if class definition not found
    if (!classDefinitions[className]) {
      return {};
    }
    
    // Process each property of the class
    classDefinitions[className].forEach(property => {
      // Ensure list and array properties are not null
      const value = generateValueForProperty(property, classDefinitions);
      
      // If property is a list/array and value is null, return empty array
      result[property.name] = (property.isList && value === null) ? [] : value;
    });
    
    return result;
  };
  
  // Generate a value based on property type
  const generateValueForProperty = (property, classDefinitions) => {
    const { fullType, baseType, genericParam, isList, name } = property;
    
    // Special case checks
    if (name.toLowerCase() === 'nullvalue') {
      return null; // Special case for NullValue property
    }
    
    // For list and collection types
    if (isList || fullType.endsWith('[]') || isCollectionType(baseType)) {
      return processCollectionType(genericParam, classDefinitions);
    }
    
    // Handle C# basic types
    switch (baseType.toLowerCase()) {
      // Numeric types
      case 'int':
      case 'int32':
      case 'int64':
      case 'long':
      case 'short':
      case 'byte':
      case 'sbyte':
      case 'uint':
      case 'ulong':
      case 'ushort':
      case 'float':
      case 'double':
      case 'decimal':
        return 0;
      
      // Boolean
      case 'bool':
      case 'boolean':
        return false;
      
      // String
      case 'string':
      case 'char':
        return "";
      
      // Guid
      case 'guid':
        return "00000000-0000-0000-0000-000000000000";
      
      // Date/Time
      case 'datetime':
      case 'datetimeoffset':
      case 'timespan':
        return new Date().toISOString();
      
      // Dictionary and similar
      case 'dictionary':
      case 'idictionary':
      case 'ireadonlydictionary':
      case 'sorteddictionary':
      case 'readonlydictionary':
      case 'concurrentdictionary':
      case 'hashtable':
        return {}; // Simple default value
        
      // Nullable types and others
      default:
        // Check for nullable types (Type?)
        if (fullType.endsWith('?')) {
          return null;
        }
        
        // Check for user-defined type (if class exists, create it)
        if (classDefinitions[baseType]) {
          return generateJsonFromClass(baseType, classDefinitions);
        }
        
        // Return empty object for unknown types
        return {};
    }
  };
  
  // Process collection types
  const processCollectionType = (genericType, classDefinitions) => {
    // Always return an array, never a null value
    if (!genericType) return [];
    
    // If genericType contains comma, it's likely a Dictionary<Key,Value> type
    if (genericType.includes(',')) {
      return {}; // Return simple empty object for Dictionary
    }
    
    // Return based on generic parameter type
    switch (genericType.toLowerCase()) {
      case 'int':
      case 'int32':
      case 'int64':
      case 'long':
      case 'short':
      case 'byte':
        return [0];
        
      case 'string':
        return [""];
        
      case 'bool':
      case 'boolean':
        return [false];
        
      case 'datetime':
      case 'datetimeoffset':
        return [new Date().toISOString()];
        
      default:
        // If it's a user-defined class
        if (classDefinitions[genericType]) {
          return [generateJsonFromClass(genericType, classDefinitions)];
        }
        
        // For unknown types, return empty object array - always return an array
        return [{}];
    }
  };
  
  // Helper function to recursively ensure null arrays are replaced with empty arrays
  const ensureArraysAreNotNull = (obj) => {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }
    
    // Check for array type
    if (Array.isArray(obj)) {
      return obj.map(item => ensureArraysAreNotNull(item));
    }
    
    // Check all properties of object type
    const result = { ...obj };
    
    Object.keys(result).forEach(key => {
      const value = result[key];
      
      // Guess if property is likely an array based on name
      const isLikelyArray = /s$|list$|array$|collection$|items$/i.test(key);
      
      if (value === null && isLikelyArray) {
        // Likely an array - use empty array instead of null
        result[key] = [];
      } else if (typeof value === 'object') {
        // Check nested objects recursively
        result[key] = ensureArraysAreNotNull(value);
      }
    });
    
    return result;
  };

  const handleCopyOutput = () => {
    if (jsonOutput) {
      navigator.clipboard.writeText(jsonOutput);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const downloadJson = () => {
    if (jsonOutput) {
      const blob = new Blob([jsonOutput], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'converted.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const clearAll = () => {
    setCsharpInput('');
    setJsonOutput('');
    setError('');
  };
  
  const loadExample = () => {
    setCsharpInput(csharpExample);
    setJsonOutput('');
    setError('');
  };

  return (
    <div className="container">
      <SEO
        title="C# Class to JSON Converter | YourJSONTools"
        description="Convert C# class definitions to JSON format. Fast, free, and online - no data sent to servers."
        keywords="c# to json, csharp json converter, convert class to json, json generator from c#, nested class json"
      />

      <h1>C# Class to JSON Converter</h1>
      <p className="tool-description">
        Convert C# class definitions to JSON format. Simply paste your C# class code and get the equivalent JSON structure. Handles nested classes and complex object hierarchies.
      </p>

      <div className="csharp-to-json-container">
        <div className="tool-section">
          <div className="editor-header">
            <h3>C# Class Input</h3>
            <div className="toolbar">
              <button onClick={loadExample} className="toolbar-button example-button">Load Example</button>
              <button onClick={clearAll} className="toolbar-button clear-button">Clear</button>
            </div>
          </div>
          
          <div className="editor-wrapper">
            <TextareaWithLines
              value={csharpInput}
              onChange={(e) => setCsharpInput(e.target.value)}
              onScroll={handleInputScroll}
              textareaRef={inputRef}
              lineNumbersRef={inputLineNumbersRef}
              placeholder="Paste your C# class here..."
            />
          </div>
          
          <div className="editor-buttons">
            <button onClick={convertCsharpToJson} className="primary-button">Convert to JSON</button>
          </div>
        </div>

        <div className="tool-section">
          <div className="editor-header">
            <h3>JSON Output</h3>
            <div className="toolbar">
              <button onClick={handleCopyOutput} className="toolbar-button copy-button">
                {copySuccess ? 'Copied!' : 'Copy'}
              </button>
              <button onClick={downloadJson} className="toolbar-button download-button">Download</button>
            </div>
          </div>
          
          <div className="editor-wrapper">
            <PreWithLines
              value={jsonOutput}
              onScroll={handleOutputScroll}
              preRef={outputRef}
              lineNumbersRef={outputLineNumbersRef}
              language="json"
            />
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <style>{`
        .csharp-to-json-container {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          margin-bottom: 2rem;
        }
        
        @media (min-width: 1024px) {
          .csharp-to-json-container {
            grid-template-columns: 1fr 1fr;
          }
        }
        
        .tool-section {
          display: flex;
          flex-direction: column;
          background: #f5f5f5;
          border-radius: 6px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        .editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          background: #eaeaea;
          border-bottom: 1px solid #ddd;
        }
        
        .editor-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: #333;
        }
        
        .toolbar {
          display: flex;
          gap: 8px;
        }
        
        .toolbar-button {
          padding: 0.4rem 0.8rem;
          font-size: 0.875rem;
          background: #fff;
          border: 1px solid #ccc;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
          color: #333;
        }
        
        .toolbar-button:hover {
          background: #f0f0f0;
        }
        
        .clear-button {
          background: #607d8b;
          color: white;
          border-color: #607d8b;
        }
        
        .clear-button:hover {
          background: #455a64;
        }
        
        .example-button {
          background: #ff9800;
          color: white;
          border-color: #ff9800;
        }
        
        .example-button:hover {
          background: #f57c00;
        }
        
        .copy-button {
          background: #4caf50;
          color: white;
          border-color: #4caf50;
        }
        
        .copy-button:hover {
          background: #388e3c;
        }
        
        .download-button {
          background: #2196f3;
          color: white;
          border-color: #2196f3;
        }
        
        .download-button:hover {
          background: #1976d2;
        }
        
        .editor-wrapper {
          flex: 1;
          min-height: 400px;
          position: relative;
        }
        
        .editor-buttons {
          padding: 1rem;
          background: #eaeaea;
          border-top: 1px solid #ddd;
          display: flex;
          justify-content: flex-end;
        }
        
        .primary-button {
          padding: 0.6rem 1.2rem;
          font-size: 0.9rem;
          font-weight: 500;
          color: white;
          background: #2196f3;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: background 0.2s;
        }
        
        .primary-button:hover {
          background: #1976d2;
        }
        
        .error-message {
          margin-top: 1rem;
          padding: 0.75rem 1rem;
          color: #d32f2f;
          background: #ffebee;
          border-radius: 4px;
          border-left: 4px solid #d32f2f;
        }
      `}</style>

      <div style={{ width: '90%', maxWidth: '1200px', margin: '40px auto 20px', padding: '0 20px', fontSize: '15px', lineHeight: '1.6', color: '#444' }}>
        <h2 style={{ fontSize: '22px', marginBottom: '15px', color: '#333' }}>About C# Class to JSON Converter</h2>
        <p style={{ marginBottom: '15px' }}>
          This tool helps you convert C# class definitions to JSON format.
          It especially handles complex nested class hierarchies and collections correctly.
        </p>
        
        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>Features:</h3>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}><strong>Nested Class Support:</strong> Full support for complex object hierarchies.</li>
          <li style={{ marginBottom: '8px' }}><strong>Collection Detection:</strong> Support for generic collections like List, IEnumerable, Collection, etc.</li>
          <li style={{ marginBottom: '8px' }}><strong>Basic C# Types:</strong> Appropriate JSON values for basic types like Int, String, Boolean, DateTime.</li>
          <li style={{ marginBottom: '8px' }}><strong>Nullable Type Support:</strong> Proper handling of nullable types (int?, bool?, etc.).</li>
          <li style={{ marginBottom: '8px' }}><strong>Root Class Detection:</strong> Uses classes containing "Root" or the first class as the root.</li>
        </ul>
        
        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>How to Use:</h3>
        <ol style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}>Paste your C# class definition in the left panel.</li>
          <li style={{ marginBottom: '8px' }}>You can use the "Load Example" button to load a sample.</li>
          <li style={{ marginBottom: '8px' }}>Click the "Convert to JSON" button.</li>
          <li style={{ marginBottom: '8px' }}>You can copy or download the generated JSON output.</li>
        </ol>
        
        <p style={{ marginBottom: '15px' }}>
          This converter performs all operations in your browser and does not send your data to any server.
          This protects your data privacy and ensures fast processing.
        </p>
      </div>

    </div>
  );
};

export default CsharpToJsonPage; 